import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, Pagination } from '@mui/material';
import classNames from 'classnames';

import Loadable from '@components/Loadable/Loadable';
import { HighRiskSupplement } from '@models/highRiskSupplement';
import { highRiskSupplementsDetailsIdSelector } from '@store/features/highRiskSupplementDetails/selectors';
import {
  highRiskSupplementSearchErrorSelector,
  highRiskSupplementSearchIsLoadingSelector,
  highRiskSupplementSearchPageSelector,
  highRiskSupplementSearchPaginationSelector,
} from '@store/features/highRiskSupplementsSearch/selectors';

import HRSItemCard from '../HRSItemCard/HRSItemCard';
import HRSSearchListEmptyCard from '../HRSSearchListEmptyCard/HRSSearchListEmptyCard';

interface IProps {
  onFetchEntriesList: () => void;
  onEntryClick: (data: HighRiskSupplement) => void;
  onPaginationChange: (page: number) => void;
}

const HRSSearchList: FC<IProps> = ({
  onFetchEntriesList,
  onEntryClick,
  onPaginationChange,
}): JSX.Element => {
  const { t } = useTranslation();
  const riskSupplementsSearchList = useSelector(highRiskSupplementSearchPageSelector);
  const riskSupplementsListSearchError = useSelector(highRiskSupplementSearchErrorSelector);
  const riskSupplementsListSearchIsLoading = useSelector(highRiskSupplementSearchIsLoadingSelector);
  const activeEntryId = useSelector(highRiskSupplementsDetailsIdSelector);
  const searchPagination = useSelector(highRiskSupplementSearchPaginationSelector);

  const handleSearchPagination = useCallback(
    (event: React.SyntheticEvent<unknown>, page: number) => {
      onPaginationChange(page);
    },
    [onPaginationChange],
  );

  return (
    <>
      <Box
        className={classNames('high-risk-supplements__list-panel', {
          'high-risk-supplements__list-panel--empty': !(
            riskSupplementsSearchList && riskSupplementsSearchList.length
          ),
        })}
      >
        <Box>
          <Loadable
            isLoading={riskSupplementsListSearchIsLoading}
            error={riskSupplementsListSearchError && t('errors.highRiskSupplementsListError')}
            onRetry={onFetchEntriesList}
          >
            {Boolean(riskSupplementsSearchList && riskSupplementsSearchList.length) &&
              riskSupplementsSearchList?.map(item => (
                <HRSItemCard
                  data={item}
                  isActive={item.id === activeEntryId}
                  onClick={onEntryClick}
                />
              ))}
            {Boolean(!riskSupplementsSearchList || !riskSupplementsSearchList.length) && (
              <HRSSearchListEmptyCard />
            )}
          </Loadable>
        </Box>
      </Box>
      {!!searchPagination?.pageCount && (
        <Pagination
          className="inquiry-list__pagination"
          color="primary"
          page={searchPagination?.page}
          count={searchPagination?.pageCount}
          onChange={handleSearchPagination}
        />
      )}
    </>
  );
};

export default HRSSearchList;
